import moment from 'moment'
import { Subscription } from '../../Profile/types'
import { getSubscriptionCreatedAt } from '../../Profile/commands/subscriptions/utils/getCheckDate'
import { UnitCancel, UnitMaster } from '../../Units/reducer'
import {
  getActiveCancellationRecord,
  getCancellationOperatorStatus,
} from '../../Units/cancellationStatus'

export type AdminBillingType = 'Card' | 'Bank' | 'Unknown'
export type AdminTransactionFilter =
  | 'all'
  | 'settled'
  | 'pending'
  | 'failed'
  | 'none'

export interface AdminProfileSummary {
  customerProfileId: number
  customerName: string
  email: string
  phoneNumber: string
  address: string
  city: string
  state: string
  zip: string
  billingType: AdminBillingType
}

export interface AdminSubscriptionContext {
  profile: AdminProfileSummary
  unit: UnitMaster
  subscription: Subscription
}

export interface AdminSubscriptionRawTransaction {
  transId?: number | string
  transactionStatus?: string
  settleAmount?: number | string
  submitTimeLocal?: string
  submitTimeUTC?: string
  accountNumber?: number | string
  accountType?: string
  invoiceNumber?: string
  description?: string
  orderDescription?: string
  subscription?: {
    id?: number | string
    subscriptionId?: number | string
  }
  subscriptionId?: number | string
}

export interface AdminSubscriptionTransactionRow {
  transId: string
  transactionStatus: string
  settleAmount: number
  submitTimeLocal: string
  accountNumber: string
  accountType: string
  invoiceNumber?: string
  subscriptionId?: string
  submittedAt: number
}

export interface AdminSubscriptionTableRow {
  id: string
  subscriptionId: string
  customerProfileId: number
  customerName: string
  email: string
  phoneNumber: string
  address: string
  city: string
  state: string
  zip: string
  unitLabel: string
  unitNumber: string
  size: string
  unitType: string
  amount: number
  startDate: string
  startDateTimestamp: number
  createdAt: string
  createdAtTimestamp: number
  status: string
  paymentType: AdminBillingType
  latestTransactionStatus: string
  latestTransactionTimestamp: number
  transactionCount: number
  isClimate: boolean
  isCovered: boolean
  pendingCancellation?: boolean
  pendingCancellationLabel?: string
  transactions: AdminSubscriptionTransactionRow[]
}

export type AdminSubscriptionSortKey =
  | 'customerName'
  | 'unitLabel'
  | 'startDate'
  | 'amount'
  | 'paymentType'
  | 'latestTransactionStatus'
  | 'transactionCount'
  | 'status'

export interface AdminSubscriptionFilters {
  search: string
  status: string
  paymentType: 'all' | AdminBillingType
  transactionStatus: AdminTransactionFilter
}

export const NO_LINKED_CHARGES_LABEL = 'No linked charges'

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

const normalizeText = (value?: string | number | null) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

const toSentenceCase = (value?: string | null) => {
  if (!value) return 'Unknown'

  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_.-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase())
}

const toMomentValue = (value?: string | null) => {
  if (!value) return null

  const parsed = moment(value)
  return parsed.isValid() ? parsed : null
}

const toTimestamp = (value?: string | null) =>
  toMomentValue(value)?.valueOf() ?? 0

const toDisplayDate = (value?: string | null, format = 'MMM D, YYYY') => {
  const parsed = toMomentValue(value)
  return parsed ? parsed.format(format) : '—'
}

const toNumber = (value?: number | string | null) => {
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  return 0
}

const inferBillingTypeFromAccountType = (
  value?: string | null
): AdminBillingType => {
  const normalized = String(value ?? '').toLowerCase()

  if (!normalized) return 'Unknown'
  if (
    normalized.includes('checking') ||
    normalized.includes('savings') ||
    normalized.includes('bank')
  ) {
    return 'Bank'
  }

  if (normalized.includes('visa') || normalized.includes('mastercard')) {
    return 'Card'
  }

  return 'Card'
}

export const formatCurrency = (value: number) =>
  currencyFormatter.format(value || 0)

export const formatTransactionStatus = (value?: string | null) =>
  toSentenceCase(value ?? 'Unknown')

export const formatSubscriptionStatus = (value?: string | null) =>
  toSentenceCase(value ?? 'Unknown')

export const formatAddress = ({
  address,
  city,
  state,
  zip,
}: Pick<AdminSubscriptionTableRow, 'address' | 'city' | 'state' | 'zip'>) => {
  const street = String(address ?? '').trim()
  const locality = [city, state].filter(Boolean).join(', ').trim()
  const postalCode = String(zip ?? '').trim()
  const localityLine = [locality, postalCode].filter(Boolean).join(' ').trim()

  return [street, localityLine].filter(Boolean).join(', ') || '—'
}

export const getUnitTypeLabel = ({
  isClimate,
  isCovered,
}: {
  isClimate?: boolean
  isCovered?: boolean
}) => {
  if (isClimate && isCovered) return 'Climate / Covered'
  if (isClimate) return 'Climate'
  if (isCovered) return 'Covered'
  return 'Standard'
}

export const getTransactionStatusBucket = (
  status?: string | null
): Exclude<AdminTransactionFilter, 'all'> => {
  const normalized = String(status ?? '').toLowerCase()

  if (!normalized) return 'none'
  if (
    normalized.includes('settled') ||
    normalized.includes('approved') ||
    normalized.includes('success')
  ) {
    return 'settled'
  }
  if (
    normalized.includes('pending') ||
    normalized.includes('held') ||
    normalized.includes('captured')
  ) {
    return 'pending'
  }
  if (
    normalized.includes('declined') ||
    normalized.includes('failed') ||
    normalized.includes('error') ||
    normalized.includes('void') ||
    normalized.includes('refund')
  ) {
    return 'failed'
  }

  return 'none'
}

const resolveTransactionSubscriptionId = (
  transaction: AdminSubscriptionRawTransaction
) => {
  const nestedId = transaction.subscription?.id
  const nestedSubscriptionId = transaction.subscription?.subscriptionId
  const directId = transaction.subscriptionId

  return String(nestedId ?? nestedSubscriptionId ?? directId ?? '') || undefined
}

const buildTransactionRows = (
  transactions: AdminSubscriptionRawTransaction[]
) =>
  transactions
    .map<AdminSubscriptionTransactionRow>((transaction, index) => {
      const submitTime =
        transaction.submitTimeLocal ?? transaction.submitTimeUTC ?? ''

      return {
        transId: String(transaction.transId ?? `transaction-${index}`),
        transactionStatus: formatTransactionStatus(
          transaction.transactionStatus
        ),
        settleAmount: toNumber(transaction.settleAmount),
        submitTimeLocal: toDisplayDate(submitTime, 'MMM D, YYYY h:mm A'),
        accountNumber: String(transaction.accountNumber ?? '—'),
        accountType: toSentenceCase(transaction.accountType ?? 'Unknown'),
        invoiceNumber: transaction.invoiceNumber,
        subscriptionId: resolveTransactionSubscriptionId(transaction),
        submittedAt: toTimestamp(submitTime),
      }
    })
    .sort((left, right) => right.submittedAt - left.submittedAt)

const matchesTransactionMetadata = (
  transaction: AdminSubscriptionTransactionRow,
  context: AdminSubscriptionContext
) => {
  const transactionTokens = [transaction.invoiceNumber, transaction.transId]
    .map((value) => normalizeText(value))
    .filter(Boolean)

  const subscriptionTokens = [
    context.subscription.order?.invoiceNumber,
    context.subscription.order?.description,
    context.subscription.name,
    context.unit.unitNumber,
    `${context.unit.size}/${context.unit.unitNumber ?? ''}`,
  ]
    .map((value) => normalizeText(value))
    .filter(Boolean)

  if (transactionTokens.length === 0 || subscriptionTokens.length === 0) {
    return false
  }

  return transactionTokens.some((token) =>
    subscriptionTokens.some(
      (candidate) =>
        token === candidate ||
        token.includes(candidate) ||
        candidate.includes(token)
    )
  )
}

const matchesByAmount = (
  transaction: AdminSubscriptionTransactionRow,
  contexts: AdminSubscriptionContext[]
) => {
  const matches = contexts.filter(
    ({ subscription }) =>
      Math.abs(toNumber(subscription.amount) - transaction.settleAmount) < 0.01
  )

  return matches.length === 1 ? matches[0] : undefined
}

export const buildAdminSubscriptionRows = ({
  contexts,
  transactionsByCustomer,
}: {
  contexts: AdminSubscriptionContext[]
  transactionsByCustomer: Record<number, AdminSubscriptionRawTransaction[]>
}): AdminSubscriptionTableRow[] => {
  const contextsByCustomer = contexts.reduce<
    Record<number, AdminSubscriptionContext[]>
  >((accumulator, context) => {
    const customerId = context.profile.customerProfileId

    accumulator[customerId] = accumulator[customerId] ?? []
    accumulator[customerId].push(context)
    return accumulator
  }, {})

  const assignments = new Map<string, AdminSubscriptionTransactionRow[]>()

  Object.entries(contextsByCustomer).forEach(
    ([customerKey, customerContexts]) => {
      const customerId = Number(customerKey)
      const subscriptionIds = new Set(
        customerContexts.map(({ subscription }) =>
          String(subscription.subscriptionId)
        )
      )
      const mappedTransactions = buildTransactionRows(
        transactionsByCustomer[customerId] ?? []
      )

      const unresolved: AdminSubscriptionTransactionRow[] = []

      mappedTransactions.forEach((transaction) => {
        const directMatchId = transaction.subscriptionId

        if (directMatchId && subscriptionIds.has(directMatchId)) {
          const existingTransactions = assignments.get(directMatchId) ?? []
          existingTransactions.push(transaction)
          assignments.set(directMatchId, existingTransactions)
          return
        }

        unresolved.push(transaction)
      })

      unresolved.forEach((transaction) => {
        let matchedContext = customerContexts.find((context) =>
          matchesTransactionMetadata(transaction, context)
        )

        if (!matchedContext) {
          matchedContext = matchesByAmount(transaction, customerContexts)
        }

        if (!matchedContext && customerContexts.length === 1) {
          matchedContext = customerContexts[0]
        }

        if (!matchedContext) return

        const subscriptionId = String(
          matchedContext.subscription.subscriptionId
        )
        const existingTransactions = assignments.get(subscriptionId) ?? []
        existingTransactions.push(transaction)
        assignments.set(subscriptionId, existingTransactions)
      })
    }
  )

  return contexts
    .map<AdminSubscriptionTableRow>((context) => {
      const subscriptionId = String(context.subscription.subscriptionId)
      const transactions = (assignments.get(subscriptionId) ?? []).sort(
        (left, right) => right.submittedAt - left.submittedAt
      )
      const latestTransaction = transactions[0]
      const createdAt = getSubscriptionCreatedAt(context.subscription)
      const startDateSource =
        context.subscription.paymentSchedule?.startDate ??
        context.subscription.startDate

      return {
        id: subscriptionId,
        subscriptionId,
        customerProfileId: context.profile.customerProfileId,
        customerName: context.profile.customerName,
        email: context.profile.email,
        phoneNumber: context.profile.phoneNumber,
        address: context.profile.address,
        city: context.profile.city,
        state: context.profile.state,
        zip: context.profile.zip,
        unitLabel:
          context.subscription.name ||
          context.subscription.order?.description ||
          `${context.unit.size}/${context.unit.unitNumber ?? '—'}`,
        unitNumber: String(context.unit.unitNumber ?? '—'),
        size: context.unit.size,
        unitType: getUnitTypeLabel(context.unit),
        amount: toNumber(context.subscription.amount),
        startDate: toDisplayDate(startDateSource),
        startDateTimestamp: toTimestamp(startDateSource),
        createdAt: toDisplayDate(createdAt),
        createdAtTimestamp: toTimestamp(createdAt),
        status: formatSubscriptionStatus(context.subscription.status),
        paymentType:
          context.profile.billingType !== 'Unknown'
            ? context.profile.billingType
            : inferBillingTypeFromAccountType(latestTransaction?.accountType),
        latestTransactionStatus:
          latestTransaction?.transactionStatus ?? NO_LINKED_CHARGES_LABEL,
        latestTransactionTimestamp: latestTransaction?.submittedAt ?? 0,
        transactionCount: transactions.length,
        isClimate: Boolean(context.unit.isClimate),
        isCovered: Boolean(context.unit.isCovered),
        transactions,
      }
    })
    .sort(
      (left, right) =>
        right.latestTransactionTimestamp - left.latestTransactionTimestamp
    )
}

export const annotateAdminSubscriptionRowsWithCancellation = ({
  rows,
  cancelUnits,
}: {
  rows: AdminSubscriptionTableRow[]
  cancelUnits: UnitCancel[]
}) =>
  rows.map<AdminSubscriptionTableRow>((row) => {
    const cancellation = getActiveCancellationRecord(
      row.subscriptionId,
      cancelUnits
    )
    const cancellationLabel = getCancellationOperatorStatus(cancellation)

    if (!cancellationLabel) return row

    return {
      ...row,
      pendingCancellation: true,
      pendingCancellationLabel: cancellationLabel,
    }
  })

export const filterAdminSubscriptionRows = (
  rows: AdminSubscriptionTableRow[],
  filters: AdminSubscriptionFilters,
  customerTransactionSearchIndex?: Map<number, string>
) => {
  const normalizedSearch = filters.search.trim().toLowerCase()

  return rows.filter((row) => {
    const customerTransactionSearch =
      customerTransactionSearchIndex?.get(row.customerProfileId) ?? ''
    const matchesSearch =
      normalizedSearch.length === 0 ||
      [
        row.customerName,
        row.email,
        row.phoneNumber,
        row.address,
        row.city,
        row.state,
        row.zip,
        row.subscriptionId,
        row.unitLabel,
        row.unitNumber,
        row.size,
        row.status,
        row.pendingCancellationLabel,
        row.paymentType,
        row.latestTransactionStatus,
        ...row.transactions.flatMap((transaction) => [
          transaction.transId,
          transaction.accountNumber,
          transaction.accountType,
          transaction.invoiceNumber,
          transaction.transactionStatus,
          transaction.submitTimeLocal,
          transaction.settleAmount,
          transaction.subscriptionId,
        ]),
        customerTransactionSearch,
      ]
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearch)

    const matchesStatus =
      filters.status === 'all' ||
      normalizeText(row.status) === normalizeText(filters.status)

    const matchesPaymentType =
      filters.paymentType === 'all' || row.paymentType === filters.paymentType

    const matchesTransactionStatus =
      filters.transactionStatus === 'all' ||
      getTransactionStatusBucket(row.latestTransactionStatus) ===
        filters.transactionStatus

    return (
      matchesSearch &&
      matchesStatus &&
      matchesPaymentType &&
      matchesTransactionStatus
    )
  })
}

export const buildCustomerTransactionSearchIndex = (
  rows: AdminSubscriptionTableRow[]
) => {
  const index = new Map<number, string[]>()

  rows.forEach((row) => {
    const currentTokens = index.get(row.customerProfileId) ?? []

    row.transactions.forEach((transaction) => {
      currentTokens.push(
        String(transaction.transId ?? ''),
        String(transaction.accountNumber ?? ''),
        String(transaction.accountType ?? ''),
        String(transaction.invoiceNumber ?? ''),
        String(transaction.transactionStatus ?? ''),
        String(transaction.submitTimeLocal ?? ''),
        String(transaction.settleAmount ?? ''),
        String(transaction.subscriptionId ?? '')
      )
    })

    index.set(row.customerProfileId, currentTokens)
  })

  return new Map(
    Array.from(index.entries()).map(([customerProfileId, tokens]) => [
      customerProfileId,
      tokens.join(' ').toLowerCase(),
    ])
  )
}

export const sortAdminSubscriptionRows = (
  rows: AdminSubscriptionTableRow[],
  sortKey: AdminSubscriptionSortKey,
  sortDirection: 'asc' | 'desc'
) => {
  const multiplier = sortDirection === 'asc' ? 1 : -1

  return [...rows].sort((left, right) => {
    let leftValue: number | string = ''
    let rightValue: number | string = ''

    switch (sortKey) {
      case 'customerName':
        leftValue = left.customerName
        rightValue = right.customerName
        break
      case 'unitLabel':
        leftValue = left.unitLabel
        rightValue = right.unitLabel
        break
      case 'startDate':
        leftValue = left.startDateTimestamp
        rightValue = right.startDateTimestamp
        break
      case 'amount':
        leftValue = left.amount
        rightValue = right.amount
        break
      case 'paymentType':
        leftValue = left.paymentType
        rightValue = right.paymentType
        break
      case 'latestTransactionStatus':
        leftValue = left.latestTransactionTimestamp
        rightValue = right.latestTransactionTimestamp
        break
      case 'transactionCount':
        leftValue = left.transactionCount
        rightValue = right.transactionCount
        break
      case 'status':
        leftValue = left.status
        rightValue = right.status
        break
      default:
        break
    }

    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      return (leftValue - rightValue) * multiplier
    }

    return String(leftValue).localeCompare(String(rightValue)) * multiplier
  })
}
